// Staffing.tsx — AWQ staffing coverage dashboard.
// Sections: KPI row, top-15 leaderboard, station coverage, apparatus utilization,
//           monthly shift trend, retention survey insights.

import { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
  Legend,
} from "recharts";
import { useAwqSummary, useRetentionThemes } from "../hooks/useStatic";
import type {
  AwqVolunteerSummary,
  AwqStationHours,
  AwqPositionBreakdown,
  AwqMonthlyTrend,
  RetentionThemeRow,
} from "../hooks/useStatic";
import ChartCard from "../components/dashboard/ChartCard";
import KPICard from "../components/dashboard/KPICard";
import StatusBadge from "../components/dashboard/StatusBadge";
import { useTheme } from "../hooks/useTheme";
import { getChartTheme } from "../lib/chart-theme";

// ── Constants ─────────────────────────────────────────────────────────────────

const DCVFD_ACCENT = "#2a7a6c";
const AMBER_RISK = "#d97706";
const RISK_TOP_N = 15;

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
}

function fmtHours(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: 1 });
}

function pct(part: number, total: number): string {
  if (total === 0) return "0%";
  return `${((part / total) * 100).toFixed(1)}%`;
}

// Parse a numeric value from theme_value, handling strings like "14" or "0.34"
function parseThemeValue(v: string | null | undefined): number {
  if (!v) return 0;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
}

// ── Leaderboard ───────────────────────────────────────────────────────────────

function LeaderboardTable({
  volunteers,
  totalHours,
  isLoading,
}: {
  volunteers: AwqVolunteerSummary[];
  totalHours: number;
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";

  if (isLoading) {
    return (
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-theme-subtle">
              {["Rank", "Volunteer", "Total Hours", "Shifts", "% of Total"].map(
                (h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 15 }).map((_, i) => (
              <tr key={i} className="border-b border-theme-subtle">
                {[30, 200, 80, 60, 60].map((w, j) => (
                  <td key={j} className="px-4 py-2.5">
                    <div
                      className="skeleton h-4 rounded-md"
                      style={{ width: w }}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  const top25 = volunteers.slice(0, 25);
  const top15Hours = volunteers
    .slice(0, RISK_TOP_N)
    .reduce((s, v) => s + v.total_hours, 0);
  const concentrationPct = totalHours > 0 ? (top15Hours / totalHours) * 100 : 0;

  return (
    <div>
      {concentrationPct > 0 && (
        <div
          className={`mx-5 mb-3 px-4 py-2.5 rounded-lg border text-sm flex items-center gap-2 ${
            isDark
              ? "bg-amber-900/20 border-amber-700/40 text-amber-300"
              : "bg-amber-50 border-amber-200 text-amber-800"
          }`}
        >
          <span className="text-base">⚠</span>
          <span>
            <strong>Concentration risk:</strong> top {RISK_TOP_N} volunteers
            account for <strong>{concentrationPct.toFixed(1)}%</strong> of all
            shift hours
          </span>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead>
            <tr className="border-b border-theme-subtle">
              {["Rank", "Volunteer", "Total Hours", "Shifts", "% of Total"].map(
                (h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider"
                  >
                    {h}
                  </th>
                ),
              )}
            </tr>
          </thead>
          <tbody>
            {top25.map((v, idx) => {
              const isRisk = idx < RISK_TOP_N;
              return (
                <tr
                  key={`${v.badge}-${idx}`}
                  className={`hover:bg-theme-overlay/50 transition-colors ${
                    isRisk
                      ? isDark
                        ? "bg-amber-900/10"
                        : "bg-amber-50/60"
                      : ""
                  }`}
                >
                  <td className="px-4 py-2.5 text-sm font-bold tabular-nums text-theme-muted w-12">
                    {idx < RISK_TOP_N ? (
                      <span style={{ color: AMBER_RISK }}>#{idx + 1}</span>
                    ) : (
                      <span className="text-theme-muted">#{idx + 1}</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-sm font-semibold text-theme-primary">
                    {v.full_name_badge ?? `Badge ${v.badge}`}
                  </td>
                  <td className="px-4 py-2.5 text-sm tabular-nums text-theme-primary font-medium">
                    {fmtHours(v.total_hours)} h
                  </td>
                  <td className="px-4 py-2.5 text-sm tabular-nums text-theme-secondary">
                    {fmt(v.shift_count)}
                  </td>
                  <td className="px-4 py-2.5 text-sm tabular-nums text-theme-secondary">
                    <div className="flex items-center gap-2">
                      <span className="w-14 text-right">
                        {pct(v.total_hours, totalHours)}
                      </span>
                      <div className="flex-1 h-1.5 rounded-full bg-theme-overlay overflow-hidden min-w-[60px] max-w-[100px]">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{
                            width: pct(v.total_hours, totalHours),
                            backgroundColor: isRisk ? AMBER_RISK : DCVFD_ACCENT,
                            opacity: 0.7,
                          }}
                        />
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Station Coverage Bar Chart ────────────────────────────────────────────────

function StationCoverageChart({
  data,
  isLoading,
}: {
  data: AwqStationHours[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  if (isLoading) {
    return <div className="skeleton rounded-xl" style={{ height: 220 }} />;
  }

  if (data.length === 0) {
    return (
      <p className="text-sm text-theme-muted text-center py-10">
        No station data available
      </p>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <BarChart
        data={data}
        margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
        barCategoryGap="20%"
      >
        <CartesianGrid
          vertical={false}
          stroke={gridColor}
          strokeDasharray="3 3"
        />
        <XAxis
          dataKey="unit"
          tick={{ fontSize: 12, fill: axisColor }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          width={50}
          tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
        />
        <Tooltip
          formatter={(value) => [`${fmtHours(Number(value))} h`, "Total Hours"]}
          contentStyle={{
            backgroundColor: getChartTheme(isDark).tooltipBg,
            border: `1px solid ${isDark ? "#3f3f46" : "#e5e7eb"}`,
            borderRadius: 8,
            fontSize: 12,
          }}
        />
        <Bar dataKey="total_hours" radius={[4, 4, 0, 0]} maxBarSize={60}>
          {data.map((_, idx) => (
            <Cell key={idx} fill={DCVFD_ACCENT} fillOpacity={0.85} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Apparatus / Position Bar Chart ────────────────────────────────────────────

function ApparatusChart({
  data,
  isLoading,
}: {
  data: AwqPositionBreakdown[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  if (isLoading) {
    return <div className="skeleton rounded-xl" style={{ height: 180 }} />;
  }

  if (data.length === 0) {
    return (
      <p className="text-sm text-theme-muted text-center py-10">
        No position data available
      </p>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={Math.max(180, data.length * 48)}>
      <BarChart
        layout="vertical"
        data={data}
        margin={{ top: 0, right: 20, left: 10, bottom: 0 }}
        barCategoryGap="20%"
      >
        <CartesianGrid
          horizontal={false}
          stroke={gridColor}
          strokeDasharray="3 3"
        />
        <XAxis
          type="number"
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
        />
        <YAxis
          type="category"
          dataKey="position"
          tick={{ fontSize: 12, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          width={90}
        />
        <Tooltip
          formatter={(value) => [`${fmt(Number(value))} shifts`, "Shift Count"]}
          contentStyle={{
            backgroundColor: getChartTheme(isDark).tooltipBg,
            border: `1px solid ${isDark ? "#3f3f46" : "#e5e7eb"}`,
            borderRadius: 8,
            fontSize: 12,
          }}
        />
        <Bar
          dataKey="shift_count"
          radius={[0, 4, 4, 0]}
          maxBarSize={28}
          fill={DCVFD_ACCENT}
          fillOpacity={0.85}
        />
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Monthly Trend Line Chart ──────────────────────────────────────────────────

function MonthlyTrendChart({
  data,
  isLoading,
}: {
  data: AwqMonthlyTrend[];
  isLoading: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  if (isLoading) {
    return <div className="skeleton rounded-xl" style={{ height: 240 }} />;
  }

  if (data.length === 0) {
    return (
      <p className="text-sm text-theme-muted text-center py-10">
        No trend data available
      </p>
    );
  }

  // Format month labels: "2023-07" → "Jul '23"
  const chartData = data.map((d) => {
    const [y, m] = (d.month ?? "").split("-");
    const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
    const label = isNaN(date.getTime())
      ? (d.month ?? "")
      : date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
    return { ...d, label };
  });

  return (
    <ResponsiveContainer width="100%" height={240}>
      <LineChart
        data={chartData}
        margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
      >
        <CartesianGrid stroke={gridColor} strokeDasharray="3 3" />
        <XAxis
          dataKey="label"
          tick={{ fontSize: 10, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          interval="preserveStartEnd"
        />
        <YAxis
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          width={42}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: getChartTheme(isDark).tooltipBg,
            border: `1px solid ${isDark ? "#3f3f46" : "#e5e7eb"}`,
            borderRadius: 8,
            fontSize: 12,
          }}
          formatter={(value, name) => [
            name === "shift_count"
              ? `${fmt(Number(value))} shifts`
              : `${fmtHours(Number(value))} h`,
            name === "shift_count" ? "Shifts" : "Hours",
          ]}
        />
        <Legend
          formatter={(val) => (val === "shift_count" ? "Shifts" : "Hours")}
          wrapperStyle={{ fontSize: 12 }}
        />
        <Line
          type="monotone"
          dataKey="shift_count"
          stroke={DCVFD_ACCENT}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
        />
        <Line
          type="monotone"
          dataKey="total_hours"
          stroke={AMBER_RISK}
          strokeWidth={2}
          dot={false}
          activeDot={{ r: 4 }}
          strokeDasharray="4 2"
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── Retention Survey Insights ─────────────────────────────────────────────────

function SurveyThemeChart({
  themes,
  themeTable,
  isLoading,
  title,
}: {
  themes: RetentionThemeRow[];
  themeTable: string;
  isLoading: boolean;
  title: string;
}) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const axisColor = isDark ? "#9ca3af" : "#6b7280";
  const gridColor = isDark ? "#27272a" : "#e5e7eb";

  const data = useMemo(() => {
    return themes
      .filter((t) => t.theme_table === themeTable)
      .map((t) => ({
        label: t.theme_label ?? "",
        value: parseThemeValue(t.theme_value),
      }))
      .sort((a, b) => b.value - a.value);
  }, [themes, themeTable]);

  if (isLoading) {
    return <div className="skeleton rounded-xl" style={{ height: 200 }} />;
  }

  if (data.length === 0) {
    return (
      <p className="text-sm text-theme-muted text-center py-6">
        No {title.toLowerCase()} data
      </p>
    );
  }

  const isRaw = data.some((d) => d.value >= 1);
  const height = Math.max(160, data.length * 44);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        layout="vertical"
        data={data}
        margin={{ top: 0, right: 30, left: 10, bottom: 0 }}
        barCategoryGap="20%"
      >
        <CartesianGrid
          horizontal={false}
          stroke={gridColor}
          strokeDasharray="3 3"
        />
        <XAxis
          type="number"
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          tickFormatter={isRaw ? undefined : (v) => `${(v * 100).toFixed(0)}%`}
        />
        <YAxis
          type="category"
          dataKey="label"
          tick={{ fontSize: 11, fill: axisColor }}
          tickLine={false}
          axisLine={false}
          width={160}
          tickFormatter={(v: string) =>
            v.length > 24 ? v.slice(0, 22) + "…" : v
          }
        />
        <Tooltip
          contentStyle={{
            backgroundColor: getChartTheme(isDark).tooltipBg,
            border: `1px solid ${isDark ? "#3f3f46" : "#e5e7eb"}`,
            borderRadius: 8,
            fontSize: 12,
          }}
          formatter={(value) => [
            isRaw
              ? `${value} mentions`
              : `${(Number(value) * 100).toFixed(0)}%`,
            title,
          ]}
          labelStyle={{ fontWeight: 600 }}
        />
        <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={28}>
          {data.map((_, idx) => (
            <Cell
              key={idx}
              fill={idx === 0 ? "#dc2626" : DCVFD_ACCENT}
              fillOpacity={0.85}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Staffing() {
  const { data: summaryData, isLoading: summaryLoading } = useAwqSummary();
  const { data: themesData, isLoading: themesLoading } = useRetentionThemes();

  const topVolunteers: AwqVolunteerSummary[] =
    summaryData?.top_volunteers ?? [];
  const stationHours: AwqStationHours[] = summaryData?.station_hours ?? [];
  const positionBreakdown: AwqPositionBreakdown[] =
    summaryData?.position_breakdown ?? [];
  const monthlyTrend: AwqMonthlyTrend[] = summaryData?.monthly_trend ?? [];
  const themes: RetentionThemeRow[] = themesData?.data ?? [];
  const totals = summaryData?.totals ?? {
    total_hours: 0,
    total_shifts: 0,
    unique_volunteers: 0,
  };

  const top1 = topVolunteers[0];
  const top15Hours = topVolunteers
    .slice(0, RISK_TOP_N)
    .reduce((s, v) => s + v.total_hours, 0);
  const concentrationPct =
    totals.total_hours > 0 ? (top15Hours / totals.total_hours) * 100 : 0;

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-7xl mx-auto space-y-6 md:space-y-8 overflow-x-hidden">
      {/* ── Page header ─────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-theme-primary">
            Staffing Coverage
          </h1>
          <p className="text-theme-secondary mt-1 text-sm">
            AWQ shift data FY24–FY25 · Dec 2024 retention survey insights
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <StatusBadge label="Static data" variant="blue" />
          <StatusBadge label="FY24–FY25" variant="green" />
        </div>
      </div>

      {/* ── KPI Row ──────────────────────────────────────────────────── */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          label="Total Volunteer Hours"
          value={summaryLoading ? "—" : fmtHours(totals.total_hours)}
          subtitle="FY24 + FY25 combined"
          accent="neutral"
          isLoading={summaryLoading}
        />
        <KPICard
          label="Active Volunteers"
          value={summaryLoading ? "—" : fmt(totals.unique_volunteers)}
          subtitle="Unique badge holders"
          accent="neutral"
          isLoading={summaryLoading}
        />
        <KPICard
          label="Top Contributor"
          value={
            summaryLoading
              ? "—"
              : top1
                ? (top1.full_name_badge?.split(" (")[0] ?? "N/A")
                : "N/A"
          }
          subtitle={
            top1
              ? `${fmtHours(top1.total_hours)} h · ${fmt(top1.shift_count)} shifts`
              : ""
          }
          accent="green"
          isLoading={summaryLoading}
        />
        <KPICard
          label="Concentration Risk"
          value={summaryLoading ? "—" : `${concentrationPct.toFixed(1)}%`}
          subtitle={`Top ${RISK_TOP_N} volunteers of total hours`}
          accent={concentrationPct > 50 ? "amber" : "neutral"}
          isLoading={summaryLoading}
        />
      </section>

      {/* ── Section 1: Top 15 Leaderboard ────────────────────────────── */}
      <ChartCard
        title="Volunteer Hours Leaderboard"
        subtitle={`Top 25 contributors · amber rows = top ${RISK_TOP_N} (concentration risk)`}
        minHeight={0}
      >
        <LeaderboardTable
          volunteers={topVolunteers}
          totalHours={totals.total_hours}
          isLoading={summaryLoading}
        />
      </ChartCard>

      {/* ── Section 2 + 3: Station Coverage + Apparatus side-by-side ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard
          title="Station Coverage"
          subtitle="Total hours by station unit"
        >
          <StationCoverageChart
            data={stationHours}
            isLoading={summaryLoading}
          />
        </ChartCard>

        <ChartCard
          title="Position Utilization"
          subtitle="Shift count by position type"
        >
          <ApparatusChart data={positionBreakdown} isLoading={summaryLoading} />
        </ChartCard>
      </div>

      {/* ── Section 4: Monthly Shift Trend ───────────────────────────── */}
      <ChartCard
        title="Monthly Shift Trend"
        subtitle="Total shifts and hours per month · Jul 2023 – Oct 2025"
      >
        <MonthlyTrendChart data={monthlyTrend} isLoading={summaryLoading} />
      </ChartCard>

      {/* ── Section 5: Retention Survey Insights ─────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard
          title="Top Discontinuation Drivers"
          subtitle="Dec 2024 survey · mention counts"
        >
          <SurveyThemeChart
            themes={themes}
            themeTable="discontinue_reasons"
            isLoading={themesLoading}
            title="Discontinuation Drivers"
          />
        </ChartCard>

        <ChartCard
          title="Top Improvement Requests"
          subtitle="Dec 2024 survey · mention counts"
        >
          <SurveyThemeChart
            themes={themes}
            themeTable="improvement_themes"
            isLoading={themesLoading}
            title="Improvement Requests"
          />
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChartCard
          title="Retention Suggestions"
          subtitle="Dec 2024 survey · % of respondents"
        >
          <SurveyThemeChart
            themes={themes}
            themeTable="retention_suggestions"
            isLoading={themesLoading}
            title="Retention Suggestions"
          />
        </ChartCard>

        <ChartCard
          title="Key Challenges Reported"
          subtitle="Dec 2024 survey · mention counts"
        >
          <SurveyThemeChart
            themes={themes}
            themeTable="challenges_themes"
            isLoading={themesLoading}
            title="Key Challenges"
          />
        </ChartCard>
      </div>
    </div>
  );
}
